import { BEATS_PER_BAR } from '../audio/plan';
import { COUNT_IN_BARS, isGuided, pressOpen, type Note } from '../core/chart';
import type { Beat } from '../core/types';
import type { Point } from '../render/view';
import { HOME, missBy } from './puck';

export type CueKind = 'title' | 'count' | 'tick' | 'hold' | 'release' | 'move';

export interface Cue {
  readonly kind: CueKind;
  readonly text: string;
  readonly key: string;
}

export type Device = 'key' | 'mouse' | 'touch';

export interface Coach {
  readonly notes: number;
  readonly still: number;
  readonly holding: boolean;
  readonly device: Device;
  readonly place: Point | null;
  readonly judged: (index: number) => boolean;
}

export const TICKS = 3;
export const RELEASE_CUE_BEATS: Beat = 1;
export const MOVE_HINTS = 2;
export const AWAY = 0.1;
export const COUNT_FROM: Beat = (COUNT_IN_BARS - 1) * BEATS_PER_BAR;
export const COUNT_TO: Beat = COUNT_IN_BARS * BEATS_PER_BAR;

const FIRST_HOLD: Readonly<Record<Device, string>> = {
  key: 'SEGURA ESPAÇO',
  mouse: 'SEGURA O CLIQUE',
  touch: 'SEGURA O DEDO',
};

const MOVE: Readonly<Record<Device, string>> = {
  key: 'LEVA ATÉ A SOMBRA',
  mouse: 'LEVA ATÉ A SOMBRA',
  touch: 'ARRASTA ATÉ A SOMBRA',
};

export function moveHints(notes: readonly Note[], coach: number, still: number): readonly number[] {
  const picked: number[] = [];
  for (const [i, note] of notes.slice(0, coach).entries()) {
    if (picked.length >= MOVE_HINTS) break;
    if (isGuided(note, still)) continue;
    if (missBy(notes[i - 1] ?? HOME, note) > AWAY) picked.push(i);
  }
  return picked;
}

function noteCue(beat: Beat, note: Note, first: boolean, coach: Coach): Cue | null {
  if (beat >= note.apexBeat && beat < note.apexBeat + RELEASE_CUE_BEATS) {
    return coach.holding ? { kind: 'release', text: 'SOLTA!', key: `release-${note.index}` } : null;
  }
  if (beat < pressOpen(note) || beat >= note.apexBeat) return null;
  const left = Math.ceil(note.apexBeat - beat);
  if (beat >= note.openBeat && left <= TICKS) {
    if (!coach.holding) return { kind: 'hold', text: 'SEGURA!', key: `hold-late-${note.index}` };
    return { kind: 'tick', text: String(left), key: `tick-${note.index}-${left}` };
  }
  if (first) return { kind: 'hold', text: FIRST_HOLD[coach.device], key: `hold-${note.index}-${coach.device}` };
  return { kind: 'hold', text: 'SEGURA', key: `hold-${note.index}` };
}

function moveCue(beat: Beat, notes: readonly Note[], i: number, coach: Coach): Cue | null {
  const note = notes[i] as Note;
  const from = (notes[i - 1]?.apexBeat ?? COUNT_TO - RELEASE_CUE_BEATS) + RELEASE_CUE_BEATS;
  if (beat < from || beat >= pressOpen(note)) return null;
  if (coach.place !== null && missBy(coach.place, note) <= AWAY) return null;
  return { kind: 'move', text: MOVE[coach.device], key: `move-${note.index}-${coach.device}` };
}

export function cueAt(
  beat: Beat,
  notes: readonly Note[],
  title: string,
  coach: Coach | null = null,
  retry: Note | null = null,
): Cue | null {
  if (!Number.isFinite(beat) || beat < 0) return null;
  if (beat < COUNT_FROM) return { kind: 'title', text: title, key: 'title' };
  if (beat < COUNT_TO) {
    const count = COUNT_TO - Math.floor(beat);
    return { kind: 'count', text: String(count), key: `count-${count}` };
  }
  if (retry !== null && beat < retry.apexBeat) {
    return { kind: 'hold', text: 'SEGURA', key: `hold-retry-${retry.index}` };
  }
  if (coach === null || !(coach.notes > 0)) return null;
  const moves = moveHints(notes, coach.notes, coach.still);
  for (const [i, note] of notes.slice(0, coach.notes).entries()) {
    if (coach.judged(note.index)) continue;
    const cue = noteCue(beat, note, i === 0, coach) ?? (moves.includes(i) ? moveCue(beat, notes, i, coach) : null);
    if (cue !== null) return cue;
  }
  return null;
}
